import { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import cn from '../../lib/cn';

/**
 * Apple HIG controls, hand-built.
 *
 * These follow the platform's own idioms rather than generic web patterns:
 * filled/tinted/plain button styles, iOS switches, grouped inset lists, and
 * hairline separators. Native elements are used wherever one already does the
 * job (button, input, select) so keyboard and assistive behaviour comes free.
 */

// ── Surfaces ────────────────────────────────────────────────────────────────

/**
 * The base card — a `systemBackground` surface on the grouped canvas, with
 * Apple's soft, low-contrast elevation rather than a hard drop shadow.
 */
export const Card = forwardRef(function Card({ className, children, hover, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-card bg-surface shadow-card',
        hover && 'transition-shadow duration-200 ease-control hover:shadow-popover',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
});

export function CardHeader({ title, subtitle, action, icon, className }) {
  return (
    <div className={cn('flex items-start justify-between gap-4 px-4 pt-3.5 pb-2.5', className)}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {icon && <span className="text-blue">{icon}</span>}
          <h3 className="text-subhead font-semibold text-label">{title}</h3>
        </div>
        {subtitle && (
          <p className="mt-1 text-footnote leading-relaxed text-label3">{subtitle}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function CardBody({ className, children }) {
  return <div className={cn('px-4 pb-4', className)}>{children}</div>;
}

/** A hairline, not a 1px rule — one device pixel on a 2x display. */
export function Divider({ className, inset }) {
  return (
    <div
      className={cn('w-full bg-separator', inset && 'ml-4', className)}
      style={{ height: '0.5px' }}
    />
  );
}

// ── Buttons ─────────────────────────────────────────────────────────────────

/**
 * Apple's button styles, by name:
 *
 * • `filled`  — the prominent action. systemBlue fill, white label.
 * • `tinted`  — secondary. A wash of the accent with a coloured label.
 * • `bordered`— macOS "bordered" push button: a light fill, no visible border.
 * • `plain`   — text only, no chrome, for tertiary actions.
 * • `destructive` — filled in systemRed.
 *
 * The scale-down on press is the platform's tap feedback, and is most of what
 * makes a button feel native rather than web.
 */
const BUTTON_VARIANTS = {
  filled: 'bg-blue text-white hover:bg-blue-600 active:bg-blue-700 shadow-card',
  tinted: 'bg-blue/12 text-blue hover:bg-blue/[0.18] active:bg-blue/25',
  bordered: 'bg-fill/[0.08] text-label hover:bg-fill/[0.14] active:bg-fill/20',
  plain: 'text-blue hover:bg-fill/[0.08] active:bg-fill/[0.14]',
  ghost: 'text-label2 hover:bg-fill/10 active:bg-fill/[0.16]',
  destructive: 'bg-red text-white hover:bg-red-600 active:bg-red-700 shadow-card',
  'destructive-tinted': 'bg-red/12 text-red hover:bg-red/[0.18] active:bg-red/25',
};

// Earlier call sites used these names. Aliased rather than churning 20 files.
BUTTON_VARIANTS.primary = BUTTON_VARIANTS.filled;
BUTTON_VARIANTS.secondary = BUTTON_VARIANTS.bordered;
BUTTON_VARIANTS.outline = BUTTON_VARIANTS.bordered;
BUTTON_VARIANTS.danger = BUTTON_VARIANTS.destructive;
BUTTON_VARIANTS['danger-outline'] = BUTTON_VARIANTS['destructive-tinted'];

const BUTTON_SIZES = {
  xs: 'h-6 px-2 text-caption gap-1 rounded-control',
  sm: 'h-7 px-2.5 text-footnote gap-1.5 rounded-control',
  md: 'h-8 px-3 text-subhead gap-1.5 rounded-field',
  lg: 'h-11 px-5 text-body gap-2 rounded-field',
};

export const Button = forwardRef(function Button({
  variant = 'bordered', size = 'md', className, children, loading, icon, disabled, ...props
}, ref) {
  return (
    <motion.button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      whileTap={disabled || loading ? undefined : { scale: 0.96 }}
      transition={{ duration: 0.09, ease: [0.4, 0, 0.2, 1] }}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-medium',
        'transition-colors duration-150 ease-control',
        'disabled:cursor-not-allowed disabled:opacity-40',
        BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.bordered,
        BUTTON_SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
      {children}
    </motion.button>
  );
});

export const IconButton = forwardRef(function IconButton({
  className, children, label, size = 'md', variant = 'ghost', ...props
}, ref) {
  const dims = { sm: 'h-6 w-6', md: 'h-7 w-7', lg: 'h-9 w-9' }[size];
  return (
    <motion.button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      whileTap={{ scale: 0.92 }}
      transition={{ duration: 0.09 }}
      className={cn(
        'inline-flex items-center justify-center rounded-control transition-colors duration-150 ease-control',
        'disabled:cursor-not-allowed disabled:opacity-35',
        BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.ghost,
        dims,
        className,
      )}
      {...props}
    >
      {children}
    </motion.button>
  );
});

// ── Badge ───────────────────────────────────────────────────────────────────

/**
 * A tinted capsule, the way Apple marks status — a wash of the system colour
 * with the colour itself as the label, never a saturated filled chip.
 */
const BADGE_TONES = {
  neutral: 'bg-fill/12 text-label2',
  muted: 'bg-fill/[0.08] text-label3',
  blue: 'bg-blue/12 text-blue',
  teal: 'bg-teal/[0.14] text-teal-600 dark:text-teal-300',
  green: 'bg-green/[0.14] text-green-600 dark:text-green-300',
  orange: 'bg-orange/[0.16] text-orange-600 dark:text-orange-300',
  red: 'bg-red/12 text-red',
  purple: 'bg-purple/12 text-purple',
};

export function Badge({ tone = 'neutral', className, children, dot, mono, size = 'sm' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full font-medium',
        size === 'xs' ? 'px-1.5 py-px text-caption2' : 'px-2 py-0.5 text-caption',
        mono && 'font-mono',
        BADGE_TONES[tone] || BADGE_TONES.neutral,
        className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/**
 * Status vocabularies recur on every screen — job card status, payment status,
 * booking status, availability, triage. Mapped once so "completed" is never
 * green here and grey two screens over.
 */
const STATUS_TONES = {
  pending: 'neutral',
  in_progress: 'blue',
  completed: 'green',
  cancelled: 'red',
  unpaid: 'orange',
  paid: 'green',
  pending_acceptance: 'orange',
  accepted: 'blue',
  rejected: 'red',
  available: 'green',
  busy: 'orange',
  away: 'muted',
  new: 'blue',
  in_review: 'orange',
  actioned: 'green',
  wont_fix: 'muted',
  spam: 'red',
  declined: 'red',
  active: 'green',
  inactive: 'orange',
  success: 'green',
  failure: 'red',
  // Field-supervisor inspections, and one garage's leg of one.
  draft: 'muted',
  ready: 'blue',
  assigned: 'orange',
  offered: 'orange',
  transferred_out: 'muted',
};

export function StatusBadge({ value, className }) {
  if (!value) return <span className="text-label4">—</span>;
  return (
    <Badge tone={STATUS_TONES[value] || 'neutral'} dot className={cn('capitalize', className)}>
      {String(value).replace(/_/g, ' ')}
    </Badge>
  );
}

// ── Fields ──────────────────────────────────────────────────────────────────

/**
 * Apple text fields are a soft inset fill with a hairline, not a boxed border.
 * On focus the accent ring appears.
 */
const FIELD_BASE = 'w-full rounded-field bg-surface2 text-label '
  + 'ring-1 ring-inset ring-separator placeholder:text-label4 '
  + 'transition-[box-shadow,background-color] duration-150 ease-control '
  + 'hover:ring-separator2 focus:outline-none focus:ring-2 focus:ring-blue '
  + 'disabled:opacity-45 disabled:cursor-not-allowed';

export const Input = forwardRef(function Input({ className, icon, size = 'md', ...props }, ref) {
  const sizing = size === 'sm' ? 'h-7 text-footnote' : 'h-8 text-subhead';
  const field = (
    <input
      ref={ref}
      className={cn(FIELD_BASE, sizing, icon ? 'pl-7 pr-2.5' : 'px-2.5', className)}
      {...props}
    />
  );

  if (!icon) return field;
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-label4">
        {icon}
      </span>
      {field}
    </div>
  );
});

export const Textarea = forwardRef(function Textarea({ className, rows = 3, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(FIELD_BASE, 'resize-y px-2.5 py-2 text-subhead leading-relaxed', className)}
      {...props}
    />
  );
});

/**
 * A native `<select>`, restyled as a macOS pop-up button.
 *
 * Deliberately not a custom listbox: the native control brings keyboard
 * behaviour, type-ahead, the platform picker on iOS and screen-reader support,
 * and nothing here needs a menu richer than that.
 */
export const Select = forwardRef(function Select({ className, children, size = 'md', ...props }, ref) {
  const sizing = size === 'sm' ? 'h-7 text-footnote' : 'h-8 text-subhead';
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          FIELD_BASE, sizing, 'cursor-pointer appearance-none pl-2.5 pr-7',
          '[&>option]:bg-surface [&>option]:text-label',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {/* The chevron PAIR Apple uses on pop-up buttons, not a single caret. */}
      <svg
        aria-hidden
        className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-label3"
        viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round"
      >
        <path d="M3.5 4.75 6 2.25l2.5 2.5M3.5 7.25 6 9.75l2.5-2.5" />
      </svg>
    </div>
  );
});

export function Field({ label, hint, error, children, className, htmlFor }) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label htmlFor={htmlFor} className="block text-footnote font-medium text-label2">
          {label}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-caption text-red">{error}</p>
      ) : hint ? (
        <p className="text-caption leading-relaxed text-label3">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * The iOS switch: a capsule that fills and slides a white knob.
 *
 * Apple's switch is systemGreen, not the accent colour — deliberate, and one of
 * the more recognisable details of the platform. Built on a real checkbox so
 * keyboard and assistive behaviour comes free.
 */
export function Switch({ checked, onChange, disabled, label, description, id }) {
  return (
    <label
      htmlFor={id}
      className={cn('flex items-start gap-2.5', disabled ? 'cursor-not-allowed opacity-45' : 'cursor-pointer')}
    >
      <span className="relative mt-px inline-flex shrink-0">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={Boolean(checked)}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
          className="peer sr-only"
        />
        <span
          className={cn(
            'block h-[22px] w-[38px] rounded-full transition-colors duration-200 ease-apple',
            'peer-focus-visible:ring-[3.5px] peer-focus-visible:ring-blue/45',
            checked ? 'bg-green' : 'bg-fill/25',
          )}
        />
        <motion.span
          aria-hidden
          className="pointer-events-none absolute left-0.5 top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow-card"
          animate={{ x: checked ? 16 : 0 }}
          transition={{ type: 'spring', stiffness: 520, damping: 34 }}
        />
      </span>
      {(label || description) && (
        <span className="min-w-0">
          {label && <span className="block text-subhead text-label">{label}</span>}
          {description && (
            <span className="mt-0.5 block text-caption leading-relaxed text-label3">{description}</span>
          )}
        </span>
      )}
    </label>
  );
}

export function Checkbox({ checked, onChange, disabled, label, id, className }) {
  return (
    <label htmlFor={id} className={cn('flex cursor-pointer items-center gap-2', disabled && 'opacity-45', className)}>
      <span className="relative inline-flex">
        <input
          id={id}
          type="checkbox"
          checked={Boolean(checked)}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
          className={cn(
            'peer h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-[5px]',
            'bg-surface2 ring-1 ring-inset ring-separator2',
            'checked:bg-blue checked:ring-0',
            'focus-visible:ring-[3.5px] focus-visible:ring-blue/45',
            'disabled:cursor-not-allowed',
          )}
        />
        <svg
          aria-hidden
          className="pointer-events-none absolute left-[3px] top-[4px] h-2.5 w-2.5 text-white opacity-0 peer-checked:opacity-100"
          viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M1.5 5.2 3.8 7.5 8.5 2.5" />
        </svg>
      </span>
      {label && <span className="text-subhead text-label">{label}</span>}
    </label>
  );
}

// ── Feedback ────────────────────────────────────────────────────────────────

/**
 * A tinted notice. Apple keeps these quiet — a wash of the system colour, a
 * hairline ring, and the colour reserved for the icon and title.
 */
const ALERT_TONES = {
  info: { wash: 'bg-blue/[0.08]', ring: 'ring-blue/20', accent: 'text-blue' },
  blue: { wash: 'bg-blue/[0.08]', ring: 'ring-blue/20', accent: 'text-blue' },
  teal: { wash: 'bg-teal/10', ring: 'ring-teal/[0.22]', accent: 'text-teal-600 dark:text-teal-300' },
  orange: { wash: 'bg-orange/10', ring: 'ring-orange/25', accent: 'text-orange-600 dark:text-orange-300' },
  red: { wash: 'bg-red/[0.08]', ring: 'ring-red/20', accent: 'text-red' },
  green: { wash: 'bg-green/10', ring: 'ring-green/[0.22]', accent: 'text-green-600 dark:text-green-300' },
};

export function Alert({ tone = 'info', title, children, icon, action, className }) {
  const style = ALERT_TONES[tone] || ALERT_TONES.info;
  return (
    <div className={cn('rounded-card px-3.5 py-3 ring-1 ring-inset', style.wash, style.ring, className)}>
      <div className="flex gap-2.5">
        {icon && <span className={cn('mt-px shrink-0', style.accent)}>{icon}</span>}
        <div className="min-w-0 flex-1">
          {title && <p className={cn('text-footnote font-semibold', style.accent)}>{title}</p>}
          {children && (
            <div className={cn('text-footnote leading-relaxed text-label2', title && 'mt-1')}>
              {children}
            </div>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </div>
  );
}

/** Shimmering placeholder in the surface colour, not a generic grey block. */
export function Skeleton({ className }) {
  return (
    <div
      className={cn(
        'animate-shimmer rounded-control bg-fill/10',
        'bg-[linear-gradient(90deg,transparent,rgb(var(--fill)/0.08),transparent)] bg-[length:400px_100%] bg-no-repeat',
        className,
      )}
    />
  );
}

/** Indeterminate progress — the thin sweeping bar from macOS. */
export function SweepBar({ active, className }) {
  return (
    <div className={cn('h-0.5 w-full overflow-hidden', className)}>
      {active && <div className="h-full w-1/4 animate-sweep rounded-full bg-blue/70" />}
    </div>
  );
}

export function Spinner({ className }) {
  return <Loader2 className={cn('h-4 w-4 animate-spin text-blue', className)} />;
}

/** Horizontal meter — used for shares, rates and utilisation. */
export function Meter({ value, max = 100, tone = 'blue', className, height = 'h-1.5' }) {
  const pct = max > 0 ? Math.min(Math.max((Number(value) / max) * 100, 0), 100) : 0;
  const tones = {
    blue: 'bg-blue', teal: 'bg-teal', green: 'bg-green',
    orange: 'bg-orange', red: 'bg-red', neutral: 'bg-label3',
  };
  return (
    <div className={cn('w-full overflow-hidden rounded-full bg-fill/[0.14]', height, className)}>
      <motion.div
        className={cn('h-full rounded-full', tones[tone] || tones.blue)}
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.65, ease: [0.32, 0.72, 0, 1] }}
      />
    </div>
  );
}

// ── Empty / error states ────────────────────────────────────────────────────

export function EmptyState({ icon, title, hint, action, className }) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      {icon && (
        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-fill/10 text-label3">
          {icon}
        </div>
      )}
      <p className="text-subhead font-medium text-label2">{title}</p>
      {hint && <p className="mt-1.5 max-w-sm text-footnote leading-relaxed text-label3">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, className }) {
  return (
    <Alert
      tone="red"
      title="Could not load this"
      className={className}
      action={onRetry && <Button size="sm" variant="tinted" onClick={onRetry}>Retry</Button>}
    >
      {error?.message || 'Unknown error'}
    </Alert>
  );
}
